/**
 * NASHGO / OTO STUDENTS — ntfy.sh Notification Service (Client-Side)
 * Dispatches real-time push notifications to ntfy.sh topic 'oto_booking'
 * with complete booking details and student profile data.
 */

export interface StudentBookingProfile {
  uid: string;
  fullName: string;
  phoneNumber?: string;
  email?: string;
  universityName?: string;
  universityId?: string;
  address?: string;
  governorate?: string;
}

export interface TripBookingInfo {
  tripId: string;
  tripCode?: string;
  universityName?: string;
  tripDate: string;
  departureTime: string;
  origin?: string;
  destination?: string;
  direction?: 'OUTBOUND' | 'RETURN' | string;
  vehiclePlate?: string;
}

export interface SingleBookingNtfyInput {
  bookingId: string;
  bookingCode: string;
  ticketId: string;
  student: StudentBookingProfile;
  trip: TripBookingInfo;
  seatNumber: number;
  price: number;
  currency?: string;
  paymentMethod: string;
  paymentStatus: string;
  bookingStatus: string;
  createdAt?: string;
}

export interface MultiBookingChildItem {
  bookingId: string;
  bookingCode: string;
  ticketId?: string;
  tripId: string;
  date: string;
  time: string;
  seatNumber: number;
  direction?: string;
  price: number;
  origin?: string;
  destination?: string;
  universityName?: string;
}

export interface MultiBookingNtfyInput {
  bookingGroupId: string;
  groupCode: string;
  bookingMode: string;
  itemCount: number;
  totalAmount: number;
  currency?: string;
  paymentMethod: string;
  paymentStatus: string;
  status: string;
  tripDates: string[];
  student: StudentBookingProfile;
  bookings: MultiBookingChildItem[];
  createdAt?: string;
}

export interface StudentRegistrationNtfyInput {
  uid?: string;
  fullName: string;
  phoneNumber: string;
  universityName?: string;
  universityGovernorate?: string;
  address?: string;
  email?: string;
  assignedRoute?: string;
  defaultPickupStation?: string;
  createdAt?: string;
  source?: 'SELF_REGISTER' | 'ADMIN_CREATED' | 'PROFILE_COMPLETED' | string;
}

const NTFY_TOPIC =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_NTFY_STUDENTS_TOPIC) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_NTFY_TOPIC) ||
  'oto_booking';
const NTFY_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_NTFY_SERVER_URL) ||
  'https://ntfy.sh';

const formatPaymentMethod = (method: string): string => {
  switch (method?.toUpperCase()) {
    case 'CASH':
      return 'نقداً عند الركوب (Cash)';
    case 'INSTAPAY':
      return 'انستاباي (InstaPay)';
    case 'WALLET':
      return 'محفظة إلكترونية (Vodafone / Orange / Etisalat / We Cash)';
    case 'ONLINE':
      return 'دفع إلكتروني بالبطاقة (Online Card)';
    case 'BRANCH':
      return 'دفع نقدي بمقر الشركة / الفرع';
    default:
      return method || 'غير محدد';
  }
};

const formatPaymentStatus = (status: string): string => {
  switch (status?.toUpperCase()) {
    case 'UNPAID':
      return 'غير مسدد (سداد نقدي عند الركوب)';
    case 'AWAITING_TRANSFER':
      return 'في انتظار تحويل إثبات السداد';
    case 'PENDING':
      return 'قيد الانتظار';
    case 'PAID':
    case 'CONFIRMED':
      return 'تم الدفع بنجاح ✅';
    default:
      return status || 'قيد المعالجة';
  }
};

const formatDirection = (dir?: string): string => {
  if (dir === 'RETURN') return 'عودة ⬅';
  return 'ذهاب ➡';
};

const formatBookingMode = (mode: string): string => {
  switch (mode?.toUpperCase()) {
    case 'MULTI_DAY':
      return 'حجز متعدد الأيام';
    case 'ROUND_TRIP':
      return 'حجز ذهاب وعودة';
    case 'MULTI_DAY_RETURN':
      return 'حجز باقة متعددة الأيام مع عودة';
    case 'SINGLE':
      return 'رحلة فردية';
    default:
      return mode || 'حجز باقة';
  }
};

const formatArabicDateTime = (isoString?: string): string => {
  try {
    const d = isoString ? new Date(isoString) : new Date();
    return d.toLocaleString('ar-EG', {
      timeZone: 'Africa/Cairo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return isoString || new Date().toISOString();
  }
};

/**
 * Dispatches real-time ntfy.sh notification for a single trip booking
 */
export const sendBookingNtfyNotification = async (
  payload: SingleBookingNtfyInput
): Promise<boolean> => {
  try {
    const { student, trip } = payload;
    const studentName = student.fullName || 'طالب غير محدد';
    const studentPhone = student.phoneNumber || 'غير متوفر';
    const university = student.universityName || trip.universityName || 'الجامعة المعتمدة';
    const address = student.address || student.governorate || 'غير مسجل';
    const origin = trip.origin || 'الإسكندرية';
    const destination = trip.destination || university;
    const direction = formatDirection(trip.direction);
    const methodText = formatPaymentMethod(payload.paymentMethod);
    const paymentStatusText = formatPaymentStatus(payload.paymentStatus);
    const bookingDateFormatted = formatArabicDateTime(payload.createdAt);

    const messageLines = [
      '🔔 تفاصيل حجز جديد عبر حساب الطالب (OTO Students)',
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      '👤 بيانات الطالب:',
      `• الاسم: ${studentName}`,
      `• رقم الهاتف: ${studentPhone}`,
      `• الجامعة: ${university}`,
      `• العنوان / المحافظة: ${address}`,
      student.email ? `• البريد الإلكتروني: ${student.email}` : null,
      `• معرّف الطالب: ${student.uid}`,
      '',
      '🚌 تفاصيل الرحلة:',
      `• كود الحجز: ${payload.bookingCode}`,
      `• رقم التذكرة: ${payload.ticketId}`,
      `• خط السير: ${origin} ⬅ ${destination}`,
      `• الاتجاه: ${direction}`,
      `• موعد الرحلة: ${trip.tripDate} (${trip.departureTime})`,
      `• رقم المقعد: 💺 ${payload.seatNumber}`,
      trip.vehiclePlate ? `• الحافلة: ${trip.vehiclePlate}` : null,
      trip.tripCode ? `• كود الرحلة: ${trip.tripCode}` : null,
      '',
      '💳 بيانات السداد:',
      `• السعر: ${payload.price} ${payload.currency || 'ج.م'}`,
      `• طريقة الدفع: ${methodText}`,
      `• حالة السداد: ${paymentStatusText}`,
      `• حالة الحجز: ${payload.bookingStatus === 'CONFIRMED' ? 'مؤكد ✅' : 'قيد المراجعة ⏳'}`,
      '',
      `⏰ وقت الحجز: ${bookingDateFormatted}`,
    ].filter(Boolean) as string[];

    const postBody = {
      topic: NTFY_TOPIC,
      title: `حجز رحلة جديد 🚌 - ${studentName}`,
      message: messageLines.join('\n'),
      priority: 4,
      tags: ['bus', 'ticket', 'tada', 'busticket'],
    };

    const res = await fetch(NTFY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify(postBody),
      signal: AbortSignal.timeout(7000),
    });

    if (!res.ok) {
      console.warn(`[ntfyService] Client dispatch failed with status ${res.status}`);
      return false;
    }

    return true;
  } catch (err: any) {
    console.warn('[ntfyService] Client notification dispatch error (non-blocking):', err?.message || err);
    return false;
  }
};

/**
 * Dispatches real-time ntfy.sh notification for a multi-day or round-trip booking package
 */
export const sendMultiBookingNtfyNotification = async (
  payload: MultiBookingNtfyInput
): Promise<boolean> => {
  try {
    const { student, bookings } = payload;
    const studentName = student.fullName || 'طالب غير محدد';
    const studentPhone = student.phoneNumber || 'غير متوفر';
    const university = student.universityName || 'الجامعة المعتمدة';
    const address = student.address || student.governorate || 'غير مسجل';
    const methodText = formatPaymentMethod(payload.paymentMethod);
    const paymentStatusText = formatPaymentStatus(payload.paymentStatus);
    const bookingModeText = formatBookingMode(payload.bookingMode);
    const bookingDateFormatted = formatArabicDateTime(payload.createdAt);

    const tripItemsLines: string[] = [];
    bookings.forEach((b, idx) => {
      const dirText = formatDirection(b.direction);
      tripItemsLines.push(
        `${idx + 1}. [${b.date} - ${b.time}] مقعد: ${b.seatNumber} (${dirText}) | ${b.origin || 'الإسكندرية'} ⬅ ${b.destination || university} (${b.price} ج.م) - كود: ${b.bookingCode}`
      );
    });

    const messageLines = [
      '🔔 حجز باقة رحلات جديدة عبر حساب الطالب (OTO Students)',
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      '👤 بيانات الطالب:',
      `• الاسم: ${studentName}`,
      `• رقم الهاتف: ${studentPhone}`,
      `• الجامعة: ${university}`,
      `• العنوان / المحافظة: ${address}`,
      student.email ? `• البريد الإلكتروني: ${student.email}` : null,
      `• معرّف الطالب: ${student.uid}`,
      '',
      '📦 تفاصيل الباقة والمجموعة:',
      `• كود المجموعة: ${payload.groupCode}`,
      `• نوع الحجز: ${bookingModeText}`,
      `• عدد الرحلات: ${payload.itemCount} رحلة`,
      `• التواريخ المحجوزة: ${payload.tripDates.join(', ')}`,
      `• المبلغ الإجمالي: ${payload.totalAmount} ${payload.currency || 'ج.م'}`,
      `• طريقة الدفع: ${methodText}`,
      `• حالة السداد: ${paymentStatusText}`,
      '',
      '📋 قائمة الرحلات والمقاعد المحجوزة:',
      ...tripItemsLines,
      '',
      `⏰ وقت الحجز: ${bookingDateFormatted}`,
    ].filter(Boolean) as string[];

    const postBody = {
      topic: NTFY_TOPIC,
      title: `حجز باقة رحلات 🚌 - ${studentName} (${payload.itemCount} رحلات)`,
      message: messageLines.join('\n'),
      priority: 4,
      tags: ['bus', 'calendar', 'tada', 'package'],
    };

    const res = await fetch(NTFY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify(postBody),
      signal: AbortSignal.timeout(7000),
    });

    if (!res.ok) {
      console.warn(`[ntfyService] Client multi-booking failed with status ${res.status}`);
      return false;
    }

    return true;
  } catch (err: any) {
    console.warn('[ntfyService] Client multi-booking dispatch error (non-blocking):', err?.message || err);
    return false;
  }
};

/**
 * Dispatches real-time ntfy.sh push notification for a newly registered student
 * including basic student details: Full Name, Phone Number, University Name, etc.
 */
export const sendStudentRegistrationNtfyNotification = async (
  payload: StudentRegistrationNtfyInput
): Promise<boolean> => {
  try {
    const studentName = payload.fullName?.trim() || 'طالب جديد';
    const rawPhone = payload.phoneNumber?.trim() || '';
    const cleanPhone = rawPhone.replace(/[^0-9]/g, '');
    let waPhone = cleanPhone;
    if (waPhone.startsWith('0')) {
      waPhone = '2' + waPhone;
    }
    const studentPhone = rawPhone || 'غير متوفر';
    const university = payload.universityName?.trim() || 'قيد الاستكمال / لم تحدد بعد';
    const governorate = payload.universityGovernorate?.trim() || '';
    const address = payload.address?.trim() || '';
    const route = payload.assignedRoute?.trim() || '';
    const station = payload.defaultPickupStation?.trim() || '';
    const formattedDate = formatArabicDateTime(payload.createdAt);

    const isCompleted = payload.source === 'PROFILE_COMPLETED';
    const notifTitle = isCompleted
      ? `✅ طالب أكّد بيانات حسابه 🎓 - ${studentName}`
      : `🎉 طالب جديد انضم للمنصة 🎓 - ${studentName}`;

    const messageLines = [
      isCompleted
        ? '✅ تم تأكيد واستكمال كافة بيانات الطالب ومحطة الركوب بنجاح عبر (OTO Students)'
        : '🔔 تسجيل طالب جديد عبر منصة (OTO Students)',
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      '👤 بيانات الطالب الأساسية:',
      `• الاسم بالكامل: ${studentName}`,
      `• رقم الهاتف (الواتساب): ${studentPhone}`,
      `• الجامعة: ${university}`,
      governorate ? `• محافظة الجامعة: ${governorate}` : null,
      address ? `• عنوان السكن / المحافظة: ${address}` : null,
      payload.email ? `• البريد الإلكتروني: ${payload.email}` : null,
      route ? `• خط السير المخصص: ${route}` : null,
      station ? `• محطة الركوب: ${station}` : null,
      payload.uid ? `• معرّف الطالب (UID): ${payload.uid}` : null,
      `• حالة الحساب: ${isCompleted ? 'مؤكد ومكتمل البيانات ✅' : 'حساب جديد قيد المتابعة ⏳'}`,
      payload.source ? `• طريقة التسجيل: ${payload.source === 'ADMIN_CREATED' ? 'تم إنشاء الحساب بواسطة الإدارة' : 'تسجيل ذاتي للطالب عبر المنصة'}` : null,
      '',
      '🏢 إحدى خدمات شركة أوتوبيسي للنقل الذكي',
      `⏰ وقت التسجيل: ${formattedDate}`,
    ].filter(Boolean) as string[];

    // Build lightweight WhatsApp action URL for ntfy to stay safely under 4KB body limit
    const actions: any[] = [];
    if (cleanPhone) {
      const waGreeting = encodeURIComponent(`السلام عليكم يا ${studentName} 👋 أهلاً ومرحباً بك في أوتو ستيودنتس (OTO Students) 🚌🎓`);
      actions.push({
        action: 'view',
        label: '💬 فتح واتساب الطالب',
        url: `https://wa.me/${waPhone}?text=${waGreeting}`,
      });
    }
    actions.push({
      action: 'view',
      label: '📋 مراجعة سجل الطلاب',
      url: 'https://otostudents.com/admin/students',
    });

    const postBody = {
      topic: NTFY_TOPIC,
      title: notifTitle,
      message: messageLines.join('\n'),
      priority: 4,
      tags: isCompleted ? ['white_check_mark', 'student', 'mortarboard'] : ['tada', 'student', 'mortarboard', 'busticket'],
      actions,
    };

    let delivered = false;

    // Stage 1: Standard POST to NTFY_URL with full payload
    try {
      const res = await fetch(NTFY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify(postBody),
        keepalive: true,
        signal: AbortSignal.timeout(4500),
      });
      if (res.ok) {
        delivered = true;
      }
    } catch {
      // Continue to stage 2
    }

    // Stage 2: Direct topic URL POST (e.g. https://ntfy.sh/oto_booking)
    if (!delivered) {
      try {
        const res2 = await fetch(`${NTFY_URL}/${NTFY_TOPIC}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json; charset=utf-8' },
          body: JSON.stringify(postBody),
          keepalive: true,
          signal: AbortSignal.timeout(4000),
        });
        if (res2.ok) {
          delivered = true;
        }
      } catch {
        // Continue to stage 3
      }
    }

    // Stage 3: Ultra-lightweight fallback without actions (guarantees delivery even on low tier / size constraints)
    if (!delivered) {
      try {
        const miniBody = {
          topic: NTFY_TOPIC,
          title: notifTitle,
          message: messageLines.join('\n'),
          priority: 4,
          tags: isCompleted ? ['white_check_mark', 'student'] : ['tada', 'student'],
        };
        const res3 = await fetch(`${NTFY_URL}/${NTFY_TOPIC}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json; charset=utf-8' },
          body: JSON.stringify(miniBody),
          keepalive: true,
          signal: AbortSignal.timeout(3500),
        });
        if (res3.ok) {
          delivered = true;
        }
      } catch {}
    }

    return delivered;
  } catch (err: any) {
    console.warn('[ntfyService] Student registration dispatch error (non-blocking):', err?.message || err);
    return false;
  }
};
